import { authClient } from "@/lib/auth/auth";
import {
  ApolloClient,
  ApolloLink,
  type DefaultOptions,
  HttpLink,
  InMemoryCache,
  from,
  gql,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import * as Sentry from "@sentry/react";

import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { BACKEND_URL, WS_BACKEND_URL } from "../../lib/constants/constants";
import { createSentryApolloLink } from "./sentry-apollo-link";

const defaultOptions: DefaultOptions = {
  watchQuery: {
    // fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    // fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

const httpLink = new HttpLink({
  uri: `${BACKEND_URL}/graphql`,
  credentials: "include",
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${WS_BACKEND_URL}/subscriptions`,
    shouldRetry: () => true,
    retryAttempts: 10,
    keepAlive: 10000,
    connectionParams: async () => {
      const { data, error } = await authClient.token();
      if (error || !data) {
        console.error("Error getting token:", error);
        throw new Error(error?.message || "Error getting token");
      }

      const token = data.token;
      const tenantId = sessionStorage.getItem("tenantId");

      // Check if both token and tenantId are available
      if (!token) {
        console.error("Missing token or tenant ID");
        throw new Error(
          "Authentication data is missing. Cannot establish WebSocket connection.",
        );
      }

      // Return the authentication data
      return {
        token: token,
        tenantId: tenantId,
      };
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink,
);

const authLink = setContext(async (req, { headers }) => {
  const tenantId = sessionStorage.getItem("tenantId");

  // Return the headers to the context so httpLink can read them
  return {
    credentials: "include",
    headers: {
      ...headers,
      "X-Tenant-ID": tenantId,
    },
  };
});

const removeTypenameLink = removeTypenameFromVariables();

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      console.error("GraphQL Error:", error);

      if (
        error.extensions?.code === "FORBIDDEN" ||
        error.message.includes("forbidden")
      ) {
        // Get the current URL to extract the company slug
        const url = window.location.pathname;
        const match = url.match(/\/([^\/]+)\//);
        const companySlug = match ? match[1] : null;

        // Determine the error message
        const errorKey =
          error.extensions?.errorKey || "http_errors.forbidden.tenant";

        // Redirect to the appropriate 403 page
        if (companySlug) {
          window.location.href = `/${companySlug}/403?error=${errorKey}`;
        } else {
          window.location.href = `/403?error=${errorKey}`;
        }

        break;
      }
    }
  }

  if (
    networkError &&
    "statusCode" in networkError &&
    networkError.statusCode === 403
  ) {
    const url = window.location.pathname;
    const match = url.match(/\/([^\/]+)\//);
    const companySlug = match ? match[1] : null;

    if (companySlug) {
      window.location.href = `/${companySlug}/403?error=http_errors.forbidden.tenant`;
    } else {
      window.location.href = `/403?error=http_errors.forbidden.tenant`;
    }
  }
});

export const sentryLogGraphQLErrorsLink = onError(
  ({ graphQLErrors, operation }) => {
    if (!graphQLErrors) return;

    graphQLErrors.forEach((error) => {
      Sentry.captureException(new Error(error.message), {
        tags: {
          operation: operation.operationName,
        },
        extra: {
          error,
          operation,
        },
      });
    });
  },
);

export const client = new ApolloClient({
  link: from([
    createSentryApolloLink(),
    removeTypenameLink,
    // dateTransformLink,
    errorLink,
    sentryLogGraphQLErrorsLink,
    authLink.concat(splitLink),
  ]),

  cache: new InMemoryCache({
    addTypename: true, // Ensures __typename is added to every object
    typePolicies: {
      InvoiceDetails: {
        keyFields: ["invoiceDetailsId"],
        fields: {
          duplicates: {
            merge(existing = [], incoming = []) {
              return [...incoming];
            },
          },
        },
      },
      LineItemDetails: {
        keyFields: ["lineItemDetailsId"],
      },
      Invoice: {
        keyFields: ["invoiceId"],
        fields: {
          transactions: {
            merge(_existing, incoming = []) {
              return [...incoming];
            },
          },
        },
      },
      Tags: {
        keyFields: ["tagId"],
      },
      Supplier: {
        keyFields: ["supplierId"],
      },
      Account: {
        keyFields: ["accountId"],
      },
      Company: {
        keyFields: ["companyId"],
      },
      TaxCode: {
        keyFields: ["taxCodeId"],
      },
      Polygon: {
        keyFields: ["polygonId"],
      },
      InvoicePolygon: {
        keyFields: ["invoicePolygonId"],
      },
      DraftPayment: {
        keyFields: ["draftPaymentId"],
      },
      LineItem: {
        keyFields: ["lineItemId"],
      },
      CompanyInfo: {
        keyFields: ["companyInfoId"],
      },
      Transaction: {
        keyFields: ["bankTxnId"],
      },
      JournalAccount: {
        keyFields: ["journalAccountId"],
      },
      BankAccount: {
        keyFields: ["bankAccountId"],
      },
      InvoiceSearchResult: {
        keyFields: ["invoiceId"],
      },
      SearchInvoicesData: {
        keyFields: ["invoiceId"],
      },
      InvoiceTransaction: {
        keyFields: ["bankTransactionId"],
      },
      WhatsAppForwarding: {
        keyFields: ["whatsappForwardingId"],
      },
    },
  }),
  defaultOptions: defaultOptions,
});

// ******************** TAGS **********************

export const TAGS_QUERY = gql`
  query {
    tags {
      tagId
      tagName
      llmRule
    }
  }
`;

export const INVOICE_TAGS_MUTATION = gql`
  mutation ($invoiceIds: [String!]!, $tagIds: [String!]!) {
    updateInvoiceTags(invoiceIds: $invoiceIds, tagIds: $tagIds)
  }
`;

// ******************** ACCOUNT BALANCES **********************

export const ACCOUNT_BALANCE = gql`
  query ($accountId: String!) {
    accountBalance(accountId: $accountId) {
      accountId
      accountNo
      accountName
      accountDescription
    }
  }
`;

// ******************** ACCOUNTS **********************

export const ACCOUNT_MUTATION = gql`
  mutation ($accountId: String!, $updatedFields: AccountInput!) {
    updateAccount(accountId: $accountId, updatedFields: $updatedFields) {
      accountId
      accountNo
      accountName
      accountDescription
      accountType
      automatic
      parentAccountId
      taxCodeId
      min
      max
      buExport
      serviceDateExport
      faktor2
    }
  }
`;

export const CREATE_ACCOUNT_MUTATION = gql`
  mutation (
    $accountNo: String
    $accountName: String
    $accountDescription: String
    $accountType: String
    $automatic: Boolean
    $parentAccountId: String
  ) {
    createAccount(
      accountNo: $accountNo
      accountName: $accountName
      accountDescription: $accountDescription
      accountType: $accountType
      automatic: $automatic
      parentAccountId: $parentAccountId
    ) {
      accountId
      accountNo
      accountName
      accountDescription
      accountType
      automatic
      parentAccountId
      min
      max
      buExport
      serviceDateExport
      faktor2
    }
  }
`;

export const DELETE_ACCOUNT_MUTATION = gql`
  mutation ($accountId: String!) {
    deleteAccount(accountId: $accountId) {
      accountId
    }
  }
`;

export const ACCOUNTS_QUERY = gql`
  query Accounts($tenantId: String!) {
    accounts(tenantId: $tenantId) {
      accountId
      accountNo
      accountName
      accountDescription
      accountType
      automatic
      parentAccountId
      taxCodeId
      min
      max
      buExport
      serviceDateExport
      faktor2
    }
  }
`;

// ******************** VAT DETAILS **********************

// ******************** TAX CODES **********************

export const TAX_CODES_QUERY = gql`
  query TaxCodes($tenantId: String!) {
    taxCodes(tenantId: $tenantId) {
      taxCodeId
      name
      description
      datevBu
      vatRate
    }
  }
`;

export const DELETE_TAX_CODES_MUTATION = gql`
  mutation ($taxCodeIds: [String!]!) {
    deleteTaxCodes(taxCodeIds: $taxCodeIds)
  }
`;

export const UPDATE_TAX_CODES_MUTATION = gql`
  mutation ($taxCodeIds: [String!]!, $updatedFields: TaxCodeInput!) {
    updateTaxCodes(taxCodeIds: $taxCodeIds, updatedFields: $updatedFields)
  }
`;

export const CREATE_TAX_CODE_MUTATION = gql`
  mutation ($taxCode: TaxCodeInput!) {
    createTaxCode(taxCode: $taxCode) {
      taxCodeId
      name
      description
      datevBu
      vatRate
    }
  }
`;

// ******************** SUPPLIER **********************

export const SUPPLIERS_QUERY = gql`
  query Suppliers($tenantId: String!, $type: String!) {
    suppliers(tenantId: $tenantId, type: $type) {
      supplierId
      supplierName
      originalSupplierName
      isCategory
      parentSupplierId
      status
      createdAt
      datevAccountNumber
      vatId
      accountId
      taxCodeId
      imported
      validationErrors
      exportedAt
      type
      customerNumber
      paymentDetails {
        IBAN
      }
      promptText
    }
  }
`;

export const UPDATE_SUPPLIER_MUTATION = gql`
  mutation ($supplierId: String!, $updatedFields: SupplierInput!) {
    updateSupplier(supplierId: $supplierId, updatedFields: $updatedFields) {
      supplierId
      supplierName
      originalSupplierName
      status
      accountId
      isCategory
      parentSupplierId
      taxCodeId
      datevAccountNumber
      address {
        addressId
        addressLine1
        city
        state
        postcode
        country
      }
      parentSupplier {
        supplierId
        supplierName
        datevAccountNumber
      }
    }
  }
`;

export const DELETE_SUPPLIERS_MUTATION = gql`
  mutation DeleteSuppliers($supplierIds: [String!]!) {
    deleteSuppliers(supplierIds: $supplierIds)
  }
`;

// ******************** EMAILS **********************

export const EMAILS_QUERY = gql`
  query {
    emails {
      emailId
      inboxId
      threadId
      snippet
      emailFrom
      emailTo
      pdfBlobName
      subject
      url
      extracted
    }
  }
`;

export const GMAIL_AUTH_QUERY = gql`
  query {
    gmailAuth {
      gmailAuthId
      accessToken
      refreshToken
      expiryTime
      emailAddress
    }
  }
`;

export const DELETE_GMAIL_AUTH_MUTATION = gql`
  mutation ($gmailAuthId: String!) {
    deleteGmailAuth(gmailAuthId: $gmailAuthId)
  }
`;

// ************* INVOICE QUICK REVIEW ****************

export const QUICK_INVOICES_REVIEW_QUERY = gql`
  query InvoiceQuickReview {
    invoiceQuickReview {
      invoiceDetailsId
      invoice {
        invoiceId
        aiSummary
        totalAmount
        currency
        dueDate
        invoiceDate
        invoiceNo
        invoiceSource
        fileBlobName
        supplierId
        split
        paid
        supplierMatch
        supplierMatchConfidence
        ocrReviewed
        createdAt
        status
        fileName
        reviewType
        serviceDate
        totalAmountConfidence
        serviceDateConfidence
        invoiceDateConfidence
        invoiceNoConfidence
        processingStatus
        validationErrors
        isBooking
        vatId
        vatIdConfidence
        isDuplicate
        documentTypeSummary
        accountIds
        accountConfidence
        taxCodeIds
        taxCodeConfidence
        costCenterIds
        errorReason {
          code
          data
          message
        }
      }
      lineItems {
        lineItemId
        buJustification
        accountId
      }
      tags {
        tagId
        tagName
        llmRule
        details
        approval
        createdAt
        updatedAt
      }
      supplier {
        supplierId
        supplierName
        status
        address {
          addressId
          addressLine1
          city
          state
          postcode
          country
        }
      }
      exports {
        datevExportId
        results
        exportedAt
      }
    }
  }
`;

// ******************** INVOICE **********************

export const UPDATE_INVOICES_STATUS_MUTATION = gql`
  mutation UpdateInvoicesStatus($invoiceIds: [String!], $status: String!) {
    updateInvoicesStatus(invoiceIds: $invoiceIds, status: $status) {
      invoiceId
      status
    }
  }
`;

export const RERUN_ACCOUNTING_PIPELINE = gql`
  mutation RerunAccountingPipeline($invoiceId: String!) {
    rerunAccountingPipeline(invoiceId: $invoiceId)
  }
`;

// ******************** SIMILAR INVOICES **********************

export const SIMILAR_INVOICES_QUERY = gql`
  query SimilarInvoices($invoiceId: String!) {
    similarInvoices(invoiceId: $invoiceId) {
      invoiceId
      supplierId
      aiSummary
      invoiceDate
      createdAt
      pdfUrl
      currency
      totalAmount
      lineItemDetails {
        lineItemDetailsId
        lineItem {
          lineItemId
          aiDescription
          description
          amount
          nettoAmount
        }
        expenseAccount {
          accountId
          accountNo
          accountName
          accountDescription
          accountType
        }
      }
      supplier {
        supplierId
        supplierName
        status
      }
    }
  }
`;

// ******************** LINE ITEM REVIEW MODE **********************

export const LINE_ITEM_REVIEW_CONFIG_QUERY = gql`
  query lineItemsReviewConfig {
    lineItemsReviewConfig {
      accounts {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      suppliers {
        supplierId
        supplierName
        accountId
        parentSupplierId
        datevAccountNumber
        status
        address {
          addressId
          addressLine1
          city
          state
          postcode
          country
        }
        childSuppliers {
          supplierId
        }
      }
    }
  }
`;

export const INVOICE_OVERVIEW_CONFIG_QUERY = gql`
  query invoiceOverviewConfig {
    invoiceOverviewConfig {
      accounts {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      suppliers {
        supplierId
        supplierName
        datevAccountNumber
      }
    }
  }
`;

export const LINE_ITEM_REVIEW_MODE_QUERY = gql`
  query lineItemsReview($tenantId: String!, $origin: String) {
    lineItemsReview(tenantId: $tenantId, origin: $origin) {
      lineItemDetails {
        lineItemDetailsId
        lineItem {
          lineItemId
          costCenterId
          aiDescription
          description
          amount
          invoiceId
          name
          vatRate
          startDate
          endDate
          accountJustification
          index
          accountConfidence
          accountReviewed
          taxCodeId
          buJustification
          taxCodeConfidence
          unlinked
        }
        costCenter {
          costCenterId
          name
          description
          identifier
        }
        expenseAccount {
          accountId
          accountNo
          accountName
          accountDescription
          accountType
          automatic
          parentAccountId
          min
          max
          buExport
          serviceDateExport
          faktor2
        }
        taxCode {
          taxCodeId
          name
          description
          datevBu
          vatRate
        }
      }
      invoiceDetails {
        invoiceDetailsId
        invoice {
          invoiceId
          totalAmount
          currency
          ocrReviewed
          invoiceText
          createdAt
          invoiceNo
          isBooking
          isDuplicate
          status
        }
        supplier {
          supplierId
          supplierName
          parentSupplierId
          datevAccountNumber
          status
        }
        tags {
          tagId
          tagName
          llmRule
          details
          approval
          createdAt
          updatedAt
        }
        duplicates {
          invoiceId
          status
          totalAmount
          invoiceNo
          createdAt
        }
      }
    }
  }
`;

// ******************** INVOICE ACTIVITY **********************
export const INVOICE_ACTIVITY_QUERY = gql`
  query InvoiceActivity($invoiceId: String!) {
    invoiceActivity(invoiceId: $invoiceId) {
      invoiceActivityId
      createdAt
      userId
      activityType
      user {
        name
      }
      changes {
        path
        type
        value
        oldValue
      }
    }
  }
`;

// ******************** LINE ITEM **********************
export const UPDATE_LINE_ITEM = gql`
  mutation ($lineItemId: String!, $updatedFields: LineItemInput!) {
    updateLineItem(lineItemId: $lineItemId, updatedFields: $updatedFields) {
      lineItemDetailsId
      lineItem {
        lineItemId
        costCenterId
        aiDescription
        amount
        invoiceId
        name
        vatRate
        startDate
        endDate
        accountJustification
        index
        description
        accountConfidence
        accountReviewed
        taxCodeId
        buJustification
        taxCodeConfidence
        unlinked
      }
      costCenter {
        costCenterId
        name
        description
        identifier
      }
      expenseAccount {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      taxCode {
        taxCodeId
        name
        description
        datevBu
        vatRate
      }
    }
  }
`;

export const UPDATE_MANY_LINE_ITEMS = gql`
  mutation UpdateManyLineItems(
    $lineItemIds: [String!]!
    $updatedFields: LineItemInput!
  ) {
    updateManyLineItems(
      lineItemIds: $lineItemIds
      updatedFields: $updatedFields
    ) {
      lineItemDetailsId
      lineItem {
        lineItemId
        costCenterId
        accountId
        aiDescription
        amount
        nettoAmount
        invoiceId
        name
        vatRate
        startDate
        endDate
        accountJustification
        index
        description
        accountConfidence
        accountReviewed
        amountConfidence
        vatRateConfidence
        taxCodeId
        buJustification
        taxCodeConfidence
        bookingText
        bookingTextManuallyUpdated
        unlinked
      }
      costCenter {
        costCenterId
        name
        description
        identifier
      }
      expenseAccount {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      taxCode {
        taxCodeId
        name
        description
        datevBu
        vatRate
      }
    }
  }
`;

export const UPDATE_LINE_ITEM_REVIEWED = gql`
  mutation UpdateManyLineItems(
    $lineItemIds: [String!]!
    $updatedFields: LineItemInput!
  ) {
    updateManyLineItems(
      lineItemIds: $lineItemIds
      updatedFields: $updatedFields
    ) {
      lineItemDetailsId
      lineItem {
        lineItemId
        accountReviewed
      }
    }
  }
`;

export const DELETE_MANY_LINE_ITEMS_MUTATION = gql`
  mutation ($lineItemIds: [String!]!) {
    deleteManyLineItems(lineItemIds: $lineItemIds)
  }
`;

export const CREATE_LINE_ITEM = gql`
  mutation CreateLineItem($invoiceId: String!, $createFields: LineItemInput!) {
    createLineItem(invoiceId: $invoiceId, createFields: $createFields) {
      lineItemDetailsId
      lineItem {
        lineItemId
        costCenterId
        aiDescription
        amount
        nettoAmount
        invoiceId
        name
        vatRate
        startDate
        endDate
        accountJustification
        index
        description
        accountConfidence
        accountReviewed
        amountConfidence
        vatRateConfidence
        accountId
        taxCodeId
        buJustification
        taxCodeConfidence
        unlinked
      }
      expenseAccount {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      taxCode {
        taxCodeId
        name
        description
        datevBu
        vatRate
      }
    }
  }
`;

// ******************** SETUP **********************

export const UPDATE_COMPANY_MUTATION = gql`
  mutation ($companyId: String!, $updatedFields: CompanyInput!) {
    updateCompany(companyId: $companyId, updatedFields: $updatedFields) {
      companyId
      companyName
      vorsteuerabzug
      hasCostCenters
      vatId
      fiscalYearStartDay
      fiscalYearStartMonth
      thirteenBType
      noGeneratedBookingTextInExportPayables
      noGeneratedBookingTextInExportReceivables
      useCustomerNumberAsDebtorAccount
      creditorAccountStart
      addresses {
        addressId
        addressLine1
        addressLine2
        city
        state
        postcode
        country
      }
    }
  }
`;

export const UPDATE_COMPANY_ADDRESSES_MUTATION = gql`
  mutation ($companyId: String!, $addresses: [CompanyAddressInput!]!) {
    updateCompanyAddresses(companyId: $companyId, addresses: $addresses) {
      addressId
      addressLine1
      addressLine2
      city
      state
      postcode
      country
    }
  }
`;

export const COMPANY_INFO_QUEREY = gql`
  query {
    companyInfo {
      companyInfoId
      companyName
      companyDescription
    }
  }
`;

export const COMPANY_QUERY = gql`
  query ($companyId: String!) {
    company(companyId: $companyId) {
      companyId
      status
      companyName
      vorsteuerabzug
      hasCostCenters
      vatId
      fiscalYearStartDay
      fiscalYearStartMonth
      thirteenBType
      accountLength
      noGeneratedBookingTextInExportPayables
      noGeneratedBookingTextInExportReceivables
      useCustomerNumberAsDebtorAccount
      creditorAccountStart
    }
  }
`;

export const DELETE_COMPANY_MUTATION = gql`
  mutation DeleteCompany($companyId: String!) {
    deleteCompany(companyId: $companyId) {
      success
      message
    }
  }
`;

export const UPDATE_COMPANY_INFO_MUTATION = gql`
  mutation ($companyInfoId: String!, $updatedFields: CompanyInfoInput!) {
    updateCompanyInfo(
      companyInfoId: $companyInfoId
      updatedFields: $updatedFields
    ) {
      companyInfoId
      companyName
      companyDescription
    }
  }
`;

export const GET_USERS_QUERY = gql`
  query GetUsersInSameAccountingFirm($withCompanyUsers: Boolean) {
    usersInSameAccountingFirm(withCompanyUsers: $withCompanyUsers) {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
      companies {
        companyId
        companyName
        role
      }
    }
  }
`;

export const COMPANY_USERS_QUERY = gql`
  query GetUsersInSameAccountingFirm {
    usersInSameAccountingFirm {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
    }
  }
`;

export const ACCOUNT_BOOKING_RULES_QUERY = gql`
  query {
    accountBookingRules {
      accountBookingRuleId
      ruleName
      ruleDescription
    }
  }
`;
